import { elizaLogger } from '@elizaos/core';
import { GraphQLResponse } from './zapperGraphQL';

/**
 * Error classification types
 */
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  GRAPHQL_ERROR = 'GRAPHQL_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

/**
 * Retry strategy configuration
 */
export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitter: boolean;
  retryableErrors: ErrorType[];
}

/**
 * Enhanced error information
 */
export interface ZapperError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  originalError?: Error;
  statusCode?: number;
  retryAfter?: number;
  timestamp: number;
  context?: Record<string, any>;
  retryable: boolean;
  suggestion?: string;
}

/**
 * Error handling configuration
 */
export interface ErrorHandlerConfig {
  enableLogging: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  enableMetrics: boolean;
  enableCircuitBreaker: boolean;
  circuitBreakerConfig?: {
    failureThreshold: number;
    resetTimeout: number;
    monitoringWindow: number;
  };
}

/**
 * Error metrics
 */
export interface ErrorMetrics {
  totalErrors: number;
  errorsByType: Record<ErrorType, number>;
  errorsBySeverity: Record<ErrorSeverity, number>;
  retryAttempts: number;
  successfulRetries: number;
  circuitBreakerTrips: number;
  averageRetryDelay: number;
  lastErrorTime: number;
}

/**
 * Circuit breaker state
 */
enum CircuitState {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  HALF_OPEN = 'HALF_OPEN',
}

/**
 * Enhanced error handler with retry logic, circuit breaker, and metrics
 */
export class ZapperErrorHandler {
  private config: ErrorHandlerConfig;
  private metrics: ErrorMetrics;
  private circuitState: CircuitState = CircuitState.CLOSED;
  private circuitFailureCount: number = 0;
  private circuitLastFailureTime: number = 0;
  private circuitNextRetryTime: number = 0;

  constructor(config: Partial<ErrorHandlerConfig> = {}) {
    this.config = {
      enableLogging: true,
      logLevel: 'error',
      enableMetrics: true,
      enableCircuitBreaker: false,
      circuitBreakerConfig: {
        failureThreshold: 5,
        resetTimeout: 60000,
        monitoringWindow: 300000,
      },
      ...config,
    };

    this.metrics = {
      totalErrors: 0,
      errorsByType: {} as Record<ErrorType, number>,
      errorsBySeverity: {} as Record<ErrorSeverity, number>,
      retryAttempts: 0,
      successfulRetries: 0,
      circuitBreakerTrips: 0,
      averageRetryDelay: 0,
      lastErrorTime: 0,
    };

    // Initialize error counters
    Object.values(ErrorType).forEach((type) => {
      this.metrics.errorsByType[type] = 0;
    });

    Object.values(ErrorSeverity).forEach((severity) => {
      this.metrics.errorsBySeverity[severity] = 0;
    });
  }

  /**
   * Process and classify an error
   */
  processError(error: Error | GraphQLResponse | any, context?: Record<string, any>): ZapperError {
    const zapperError = this.classifyError(error, context);

    if (this.config.enableMetrics) {
      this.updateMetrics(zapperError);
    }

    if (this.config.enableLogging) {
      this.logError(zapperError);
    }

    return zapperError;
  }

  /**
   * Execute function with retry logic
   */
  async executeWithRetry<T>(
    fn: () => Promise<T>,
    retryConfig: Partial<RetryConfig> = {},
    context?: Record<string, any>
  ): Promise<T> {
    const config: RetryConfig = {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2,
      jitter: true,
      retryableErrors: [
        ErrorType.NETWORK_ERROR,
        ErrorType.TIMEOUT_ERROR,
        ErrorType.RATE_LIMIT_ERROR,
      ],
      ...retryConfig,
    };

    // Check circuit breaker
    if (this.config.enableCircuitBreaker && this.circuitState === CircuitState.OPEN) {
      if (Date.now() < this.circuitNextRetryTime) {
        throw new Error(
          `Circuit breaker is OPEN. Next retry at ${new Date(this.circuitNextRetryTime).toISOString()}`
        );
      } else {
        this.circuitState = CircuitState.HALF_OPEN;
        this.logCircuitBreakerState('HALF_OPEN');
      }
    }

    let lastError: ZapperError | null = null;
    let totalDelay = 0;

    for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
      try {
        const result = await fn();

        // Success - reset circuit breaker if needed
        if (this.config.enableCircuitBreaker && this.circuitState === CircuitState.HALF_OPEN) {
          this.circuitState = CircuitState.CLOSED;
          this.circuitFailureCount = 0;
          this.logCircuitBreakerState('CLOSED');
        }

        // Update metrics for successful retry
        if (attempt > 1 && this.config.enableMetrics) {
          this.metrics.successfulRetries++;
          this.metrics.averageRetryDelay = totalDelay / (attempt - 1);
        }

        return result;
      } catch (error) {
        lastError = this.processError(error, {
          ...context,
          attempt,
          maxAttempts: config.maxAttempts,
        });

        // Check if error is retryable
        if (!config.retryableErrors.includes(lastError.type)) {
          this.handleCircuitBreakerOnError(lastError);
          throw this.createEnhancedError(lastError);
        }

        // Don't retry on last attempt
        if (attempt === config.maxAttempts) {
          this.handleCircuitBreakerOnError(lastError);
          break;
        }

        // Calculate delay
        const delay = this.calculateRetryDelay(attempt, config);
        totalDelay += delay;

        if (this.config.enableMetrics) {
          this.metrics.retryAttempts++;
        }

        if (this.config.enableLogging) {
          elizaLogger.warn(
            `Retry attempt ${attempt}/${config.maxAttempts} failed, retrying in ${delay}ms`,
            {
              error: lastError.message,
              delay,
              attempt,
              maxAttempts: config.maxAttempts,
            }
          );
        }

        // Wait before retry
        await this.delay(delay);
      }
    }

    // All retries failed
    if (lastError) {
      this.handleCircuitBreakerOnError(lastError);
      throw this.createEnhancedError(lastError);
    }

    throw new Error('Execution failed after all retry attempts');
  }

  /**
   * Get error metrics
   */
  getMetrics(): ErrorMetrics {
    return { ...this.metrics };
  }

  /**
   * Reset error metrics
   */
  resetMetrics(): void {
    this.metrics = {
      totalErrors: 0,
      errorsByType: {} as Record<ErrorType, number>,
      errorsBySeverity: {} as Record<ErrorSeverity, number>,
      retryAttempts: 0,
      successfulRetries: 0,
      circuitBreakerTrips: 0,
      averageRetryDelay: 0,
      lastErrorTime: 0,
    };

    // Reinitialize counters
    Object.values(ErrorType).forEach((type) => {
      this.metrics.errorsByType[type] = 0;
    });

    Object.values(ErrorSeverity).forEach((severity) => {
      this.metrics.errorsBySeverity[severity] = 0;
    });
  }

  /**
   * Get circuit breaker status
   */
  getCircuitBreakerStatus(): {
    state: CircuitState;
    failureCount: number;
    nextRetryTime: number;
    isOpen: boolean;
  } {
    return {
      state: this.circuitState,
      failureCount: this.circuitFailureCount,
      nextRetryTime: this.circuitNextRetryTime,
      isOpen: this.circuitState === CircuitState.OPEN,
    };
  }

  /**
   * Manually reset circuit breaker
   */
  resetCircuitBreaker(): void {
    this.circuitState = CircuitState.CLOSED;
    this.circuitFailureCount = 0;
    this.circuitNextRetryTime = 0;
    this.logCircuitBreakerState('CLOSED');
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Classify error type and severity
   */
  private classifyError(
    error: Error | GraphQLResponse | any,
    context?: Record<string, any>
  ): ZapperError {
    const timestamp = Date.now();
    let type: ErrorType;
    let severity: ErrorSeverity;
    let message: string;
    let statusCode: number | undefined;
    let retryAfter: number | undefined;
    let suggestion: string | undefined;

    // Handle GraphQL errors
    if (error.errors && Array.isArray(error.errors)) {
      type = ErrorType.GRAPHQL_ERROR;
      severity = ErrorSeverity.MEDIUM;
      message = error.errors.map((e: any) => e.message).join('; ');
      suggestion = this.getGraphQLErrorSuggestion(error.errors);
    }
    // Handle network errors
    else if (error.name === 'AbortError' || error.message?.includes('timeout')) {
      type = ErrorType.TIMEOUT_ERROR;
      severity = ErrorSeverity.MEDIUM;
      message = error.message || 'Request timed out';
      suggestion = 'Consider increasing timeout or checking network connectivity';
    }
    // Handle HTTP errors
    else if (error.status || error.statusCode) {
      statusCode = error.status || error.statusCode;

      if (statusCode === 401 || statusCode === 403) {
        type = ErrorType.AUTHENTICATION_ERROR;
        severity = ErrorSeverity.HIGH;
        message = 'Authentication failed - check API key';
        suggestion = 'Verify your API key is valid and has the required permissions';
      } else if (statusCode === 429) {
        type = ErrorType.RATE_LIMIT_ERROR;
        severity = ErrorSeverity.MEDIUM;
        message = 'Rate limit exceeded';
        retryAfter = this.extractRetryAfter(error);
        suggestion = retryAfter
          ? `Wait ${retryAfter}ms before retrying`
          : 'Reduce request frequency or implement exponential backoff';
      } else if (statusCode >= 500) {
        type = ErrorType.NETWORK_ERROR;
        severity = ErrorSeverity.HIGH;
        message = `Server error: ${statusCode}`;
        suggestion = 'Server is experiencing issues, retry with exponential backoff';
      } else {
        type = ErrorType.UNKNOWN_ERROR;
        severity = ErrorSeverity.MEDIUM;
        message = error.message || `HTTP error: ${statusCode}`;
      }
    }
    // Handle validation errors
    else if (error.name === 'ValidationError' || error.message?.includes('validation')) {
      type = ErrorType.VALIDATION_ERROR;
      severity = ErrorSeverity.LOW;
      message = error.message || 'Validation failed';
      suggestion = 'Check input parameters and format';
    }
    // Handle network connectivity errors
    else if (
      error.code === 'ECONNRESET' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ETIMEDOUT'
    ) {
      type = ErrorType.NETWORK_ERROR;
      severity = ErrorSeverity.HIGH;
      message = `Network error: ${error.code}`;
      suggestion = 'Check network connectivity and DNS resolution';
    }
    // Default case
    else {
      type = ErrorType.UNKNOWN_ERROR;
      severity = ErrorSeverity.MEDIUM;
      message = error.message || 'Unknown error occurred';
    }

    return {
      type,
      severity,
      message,
      originalError: error instanceof Error ? error : undefined,
      statusCode,
      retryAfter,
      timestamp,
      context,
      retryable: this.isRetryable(type, statusCode),
      suggestion,
    };
  }

  /**
   * Check if error is retryable
   */
  private isRetryable(type: ErrorType, statusCode?: number): boolean {
    const retryableTypes = [
      ErrorType.NETWORK_ERROR,
      ErrorType.TIMEOUT_ERROR,
      ErrorType.RATE_LIMIT_ERROR,
    ];

    if (retryableTypes.includes(type)) {
      return true;
    }

    // Some HTTP errors are retryable
    if (statusCode && statusCode >= 500) {
      return true;
    }

    return false;
  }

  /**
   * Update error metrics
   */
  private updateMetrics(error: ZapperError): void {
    this.metrics.totalErrors++;
    this.metrics.errorsByType[error.type]++;
    this.metrics.errorsBySeverity[error.severity]++;
    this.metrics.lastErrorTime = error.timestamp;
  }

  /**
   * Log error based on configuration
   */
  private logError(error: ZapperError): void {
    const logData = {
      type: error.type,
      severity: error.severity,
      message: error.message,
      statusCode: error.statusCode,
      retryable: error.retryable,
      suggestion: error.suggestion,
      context: error.context,
    };

    switch (this.config.logLevel) {
      case 'debug':
        elizaLogger.debug('Zapper error occurred', logData);
        break;
      case 'info':
        elizaLogger.info('Zapper error occurred', logData);
        break;
      case 'warn':
        elizaLogger.warn('Zapper error occurred', logData);
        break;
      case 'error':
      default:
        elizaLogger.error('Zapper error occurred', logData);
        break;
    }
  }

  /**
   * Handle circuit breaker on error
   */
  private handleCircuitBreakerOnError(error: ZapperError): void {
    if (!this.config.enableCircuitBreaker) return;

    const now = Date.now();
    const config = this.config.circuitBreakerConfig!;

    // Only count failures for certain error types
    if (error.severity === ErrorSeverity.HIGH || error.severity === ErrorSeverity.CRITICAL) {
      this.circuitFailureCount++;
      this.circuitLastFailureTime = now;

      if (this.circuitFailureCount >= config.failureThreshold) {
        this.circuitState = CircuitState.OPEN;
        this.circuitNextRetryTime = now + config.resetTimeout;
        this.metrics.circuitBreakerTrips++;
        this.logCircuitBreakerState('OPEN');
      }
    }
  }

  /**
   * Log circuit breaker state changes
   */
  private logCircuitBreakerState(state: CircuitState): void {
    if (this.config.enableLogging) {
      elizaLogger.info(`Circuit breaker state changed to ${state}`, {
        failureCount: this.circuitFailureCount,
        nextRetryTime: this.circuitNextRetryTime,
        threshold: this.config.circuitBreakerConfig?.failureThreshold,
      });
    }
  }

  /**
   * Calculate retry delay with exponential backoff and jitter
   */
  private calculateRetryDelay(attempt: number, config: RetryConfig): number {
    let delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);

    // Cap at max delay
    delay = Math.min(delay, config.maxDelay);

    // Add jitter to prevent thundering herd
    if (config.jitter) {
      delay *= 0.5 + Math.random() * 0.5;
    }

    return Math.floor(delay);
  }

  /**
   * Extract retry-after header value
   */
  private extractRetryAfter(error: any): number | undefined {
    const retryAfter = error.headers?.['retry-after'] || error.retryAfter;

    if (retryAfter) {
      const parsed = parseInt(retryAfter, 10);
      return isNaN(parsed) ? undefined : parsed * 1000; // Convert to milliseconds
    }

    return undefined;
  }

  /**
   * Get suggestion for GraphQL errors
   */
  private getGraphQLErrorSuggestion(errors: any[]): string | undefined {
    if (!errors || errors.length === 0) return undefined;

    const firstError = errors[0];
    const message = firstError.message?.toLowerCase() || '';

    if (message.includes('field') && message.includes('not exist')) {
      return 'Check field names in your GraphQL query';
    }

    if (message.includes('variable') && message.includes('required')) {
      return 'Ensure all required variables are provided';
    }

    if (message.includes('syntax')) {
      return 'Check GraphQL query syntax';
    }

    if (message.includes('depth') || message.includes('complexity')) {
      return 'Reduce query complexity or nesting depth';
    }

    return 'Check GraphQL query structure and variables';
  }

  /**
   * Create enhanced error with additional context
   */
  private createEnhancedError(zapperError: ZapperError): Error {
    const enhancedError = new Error(zapperError.message);
    enhancedError.name = `ZapperError_${zapperError.type}`;

    // Add custom properties
    Object.assign(enhancedError, {
      type: zapperError.type,
      severity: zapperError.severity,
      statusCode: zapperError.statusCode,
      retryable: zapperError.retryable,
      suggestion: zapperError.suggestion,
      context: zapperError.context,
      timestamp: zapperError.timestamp,
    });

    return enhancedError;
  }

  /**
   * Delay utility for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Default error handler instance
 */
export const defaultErrorHandler = new ZapperErrorHandler();

/**
 * Utility functions for error handling
 */
export const ErrorHandlerUtils = {
  /**
   * Check if error is retryable
   */
  isRetryable(error: any): boolean {
    if (error.retryable !== undefined) {
      return error.retryable;
    }

    // Check common retryable conditions
    if (error.code === 'ECONNRESET' || error.code === 'ETIMEDOUT') {
      return true;
    }

    if (error.status >= 500 || error.status === 429) {
      return true;
    }

    return false;
  },

  /**
   * Extract error message from various error types
   */
  extractMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error.message) {
      return error.message;
    }

    if (error.errors && Array.isArray(error.errors)) {
      return error.errors.map((e: any) => e.message).join('; ');
    }

    return 'Unknown error occurred';
  },

  /**
   * Check if error is a GraphQL error
   */
  isGraphQLError(error: any): boolean {
    return !!(error && error.errors && Array.isArray(error.errors));
  },

  /**
   * Check if error is a network error
   */
  isNetworkError(error: any): boolean {
    return !!(
      error &&
      (error.code === 'ECONNRESET' ||
        error.code === 'ENOTFOUND' ||
        error.code === 'ETIMEDOUT' ||
        error.name === 'AbortError')
    );
  },

  /**
   * Check if error is a rate limit error
   */
  isRateLimitError(error: any): boolean {
    return error.status === 429 || error.statusCode === 429;
  },

  /**
   * Get retry delay from error
   */
  getRetryDelay(error: any): number | undefined {
    if (error.retryAfter) {
      return typeof error.retryAfter === 'number'
        ? error.retryAfter
        : parseInt(error.retryAfter, 10);
    }

    if (error.headers?.['retry-after']) {
      return parseInt(error.headers['retry-after'], 10) * 1000;
    }

    return undefined;
  },
};

/**
 * Default export for convenience
 */
export default {
  ZapperErrorHandler,
  defaultErrorHandler,
  ErrorHandlerUtils,
  ErrorType,
  ErrorSeverity,
};
